interface RemoteClipboard {
  read(): Promise<string>;
  write(text: string): Promise<void>;
}
interface DesktopKeys { sendKey(keysym: number, code?: string, down?: boolean): void; focus(): void }
export type ClipboardStatus = 'ready' | 'copied' | 'pasted' | 'blocked' | 'failed';

/** Clipboard traffic stays between this device and its human-controlled desktop. */
export function bindDesktopClipboard(surface: HTMLElement, keyboard: HTMLTextAreaElement, keys: DesktopKeys, remote: RemoteClipboard, status: (value: ClipboardStatus) => void) {
  let alive = true, polling = false, lastRemote: string | undefined, lastLocal: string | undefined;
  let queue = Promise.resolve(), operation = 0;
  const focused = () => document.visibilityState !== 'hidden' && document.hasFocus() && (surface.contains(document.activeElement) || document.activeElement === keyboard);
  const pause = (ms: number) => new Promise<void>(resolve => window.setTimeout(resolve, ms));
  const shortcut = (key: string) => {
    if (!alive) return;
    // Release host modifiers before synthesizing the Linux shortcut (also supports macOS Cmd).
    keys.sendKey(0xffeb, 'MetaLeft', false); keys.sendKey(0xffe3, 'ControlLeft', false);
    keys.sendKey(0xffe3, 'ControlLeft', true); keys.sendKey(key.charCodeAt(0), `Key${key.toUpperCase()}`); keys.sendKey(0xffe3, 'ControlLeft', false);
  };
  const writeLocal = async (text: string) => {
    if (!alive || !focused()) return;
    await navigator.clipboard.writeText(text);
    if (alive) { lastLocal = text; status('copied'); }
  };
  const onPaste = (event: ClipboardEvent) => {
    if (!alive || !event.clipboardData?.types.includes('text/plain')) return;
    event.preventDefault(); event.stopImmediatePropagation();
    const text = event.clipboardData.getData('text/plain'); operation++;
    queue = queue.catch(() => undefined).then(async () => {
      if (!alive) return;
      await remote.write(text);
      if (!alive) return;
      lastLocal = lastRemote = text; shortcut('v'); keys.focus(); status('pasted');
      await pause(100);
    }).catch(() => { if (alive) status('failed'); });
    keyboard.value = ' ';
  };
  const onKey = (event: KeyboardEvent) => {
    const key = event.key.toLowerCase();
    if (!(event.ctrlKey || event.metaKey) || event.altKey || !['c', 'x', 'v'].includes(key)) return;
    event.stopImmediatePropagation();
    if (event.type === 'keyup') return;
    if (key === 'v') {
      // Keep the browser's native paste gesture: it supplies text without clipboard-read permission.
      // A focused text control also supports browser Edit > Paste and Safari's native paste menu.
      keyboard.focus({ preventScroll: true }); return;
    }
    event.preventDefault(); if (event.repeat) return;
    operation++; shortcut(key);
    const result = pause(150).then(async () => {
      if (!alive) throw new Error('Control changed');
      const text = await remote.read();
      if (!alive) throw new Error('Control changed');
      lastRemote = text; return text;
    });
    // Reserve the clipboard write during the key gesture, before the remote round trip (Safari).
    const copied = typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write
      ? navigator.clipboard.write([new ClipboardItem({ 'text/plain': result.then(text => new Blob([text], { type: 'text/plain' })) })])
      : result.then(writeLocal);
    void copied.then(() => { if (alive) status('copied'); }).catch(() => { if (alive) status('blocked'); });
    void result.catch(() => undefined);
  };
  const sync = async () => {
    if (!alive || !focused() || polling) return;
    polling = true; const version = operation;
    try {
      const text = await remote.read();
      if (!alive || !focused() || version !== operation) return;
      const changed = lastRemote !== undefined && lastRemote !== text;
      lastRemote = text;
      if (changed) {
        try { await writeLocal(text); } catch { if (alive) status('blocked'); }
        return;
      }
      // Poll local text only when permission is already granted; ordinary paste needs no grant.
      const permission = await navigator.permissions?.query({ name: 'clipboard-read' as PermissionName }).catch(() => undefined);
      if (!alive || !focused() || version !== operation || permission?.state !== 'granted') return;
      const local = await navigator.clipboard.readText();
      if (!alive || !focused() || version !== operation) return;
      if (local !== lastLocal) { lastLocal = local; if (local !== lastRemote) { await remote.write(local); if (alive) lastRemote = local; } }
    } catch { /* A polling failure must not steal focus or flood the interface; explicit actions report errors. */ }
    finally { polling = false; }
  };
  for (const target of [surface, keyboard]) {
    target.addEventListener('paste', onPaste, true);
    target.addEventListener('keydown', onKey, true); target.addEventListener('keyup', onKey, true);
  }
  const onFocus = () => { void sync(); };
  surface.addEventListener('focusin', onFocus);
  document.addEventListener('clipboardchange', onFocus);
  const timer = window.setInterval(() => { void sync(); }, 900);
  return () => {
    alive = false; operation++; clearInterval(timer);
    for (const target of [surface, keyboard]) {
      target.removeEventListener('paste', onPaste, true);
      target.removeEventListener('keydown', onKey, true); target.removeEventListener('keyup', onKey, true);
    }
    surface.removeEventListener('focusin', onFocus); document.removeEventListener('clipboardchange', onFocus);
    lastRemote = lastLocal = undefined;
  };
}
